import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../generic/Core'
import illustrations from '../../../assets/illustrations.jsx'
import useTranslation from '../../../hooks/useTranslation'
import { base } from '../../../styles/themes.jsx'
import { media } from '../../../styles/media.jsx'
import { widgetFont, widgetSize } from '../widgetTypography'

/**
 * InfoCardWidget — a promotional card: a heading block, an illustration and a
 * row of navigation buttons.
 *
 * The card paints its own background (`neutral`, `primary` or `secondary`).
 * The two accent backgrounds are step 3 of the colour scale rather than the
 * button colour, so the card reads as a tinted sibling of the widgets around
 * it rather than a banner — pale in light mode, deep and muted in dark — and
 * ordinary `font.color` text and stock button variants keep working on top.
 *
 * `image.layout` picks one of three arrangements. The first two are grid areas
 * over the same three children, so only the template changes:
 *
 *   inline   its own row, between the text and the buttons        (default)
 *   side     a column of its own, beside the text and buttons
 *   float    out of flow, anchored to the card, over everything else
 *
 * `position` (horizontal) and `align` (vertical) place it within whichever of
 * those it is using, and `offset` nudges it from there.
 *
 * A `side` card demotes itself back to `inline` on two triggers. A container
 * query catches the card being too narrow for two columns — users drag-resize
 * these on the dashboard, so the config cannot know how wide the card ends up,
 * and what matters there is the width of this card rather than of the window. A
 * phone media query catches the rest: a landscape phone can hand the card more
 * than the container threshold and still be a phone. Both point at the same
 * stacked template, so they cannot disagree.
 */

const ALIGNMENT = { left: 'flex-start', center: 'center', right: 'flex-end' }
const alignment = (position) => ALIGNMENT[position] || ALIGNMENT.left

const CROSS = { top: 'flex-start', center: 'center', bottom: 'flex-end' }
const crossAlignment = (align) => CROSS[align] || CROSS.center

// Below this the two columns of a `side` card are both too narrow to be worth it.
const SIDE_MIN_WIDTH = '420px'

// A number is pixels, a string is whatever CSS length the config wrote.
const length = (value, fallback) =>
    value == null ? fallback : typeof value === 'number' ? `${value}px` : value

const surfaceColor = (theme, background) =>
    background === 'primary' ? theme.card.primarySurface
        : background === 'secondary' ? theme.card.secondarySurface
            : theme.font.background

/*
 * Where a floating illustration hangs off the card. Each axis anchors to an
 * edge, or to the middle — and the middle is the only case needing a translate,
 * since `left: 50%` puts the box's left edge at the centre, not its middle.
 */
const floatAnchor = (position, align) => {
    const centredX = position === 'center'
    const centredY = align !== 'top' && align !== 'bottom'
    const x = centredX ? 'left: 50%;' : position === 'left' ? 'left: 1rem;' : 'right: 1rem;'
    const y = centredY ? 'top: 50%;' : align === 'top' ? 'top: 1rem;' : 'bottom: 1rem;'
    const shift = `translate(${centredX ? '-50%' : '0'}, ${centredY ? '-50%' : '0'})`
    return `${x} ${y} ${centredX || centredY ? `transform: ${shift};` : ''}`
}

/* The stacked arrangement: heading, artwork, buttons. Also what `side` falls back to. */
const stacked = `
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr auto;
  grid-template-areas: "header" "figure" "buttons";
`

/* The artwork out of flow: the text and buttons keep the whole width. */
const floated = `
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  grid-template-areas: "header" "buttons";
`

/* The artwork in its own column, spanning the height of the other two. */
const sideBySide = (position) => position === 'left' ? `
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-rows: auto 1fr;
  grid-template-areas: "figure header" "figure buttons";
` : `
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: auto 1fr;
  grid-template-areas: "header figure" "buttons figure";
`

/*
 * The card's query container, and the reason it is a separate element: an
 * element is never its own container, so `container-type` on the Card would
 * only serve the Card's descendants and its own `@container` rule would find no
 * ancestor container and silently never match.
 */
const Frame = styled.div`
  container-type: inline-size;
  height: 100%;
`

const Card = styled.div`
  position: relative;
  display: grid;
  gap: ${base.space[3]};
  height: 100%;
  padding: ${base.space[4]};
  box-sizing: border-box;
  border-radius: 0.75rem;
  overflow: hidden;
  color: ${({ theme }) => theme.font.color};
  background: ${({ theme, $background }) => $background === 'neutral' ? 'transparent' : surfaceColor(theme, $background)};
  /*
   * The colour the illustration's cut-out shapes are filled with. Always a
   * plain colour: card.background is a gradient under the 'neo' style, and a
   * gradient in an svg fill is not a colour at all.
   */
  --illu-surface: ${({ theme, $background }) => surfaceColor(theme, $background)};

  ${({ $layout, $position }) => $layout === 'float' ? floated
        : $layout === 'side' ? sideBySide($position)
            : stacked}

  ${({ $layout }) => $layout !== 'side' ? '' : `
    @container (max-width: ${SIDE_MIN_WIDTH}) {
      ${stacked}
    }
    ${media.phone} {
      ${stacked}
    }
  `}
`

const Header = styled.div`
  grid-area: header;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${base.space[1]};
  align-items: ${({ $position }) => alignment($position)};
  text-align: ${({ $position }) => $position || 'left'};
`

const CardTitle = styled.h3`
  margin: 0;
  ${widgetFont.title}
  color: inherit;
`

/* A div rather than a p: GridWidget styles every p inside a widget. */
const Description = styled.div`
  ${widgetFont.body}
  color: ${({ theme }) => theme.font.label};
`

/*
 * In flow the figure fills whichever grid area it was given. Floating, it
 * leaves the grid entirely and anchors to the card's padding box — behind the
 * text and buttons, and never intercepting a click meant for them.
 */
const Figure = styled.div`
  grid-area: figure;
  display: flex;
  min-width: 0;
  min-height: 0;
  align-items: ${({ $align }) => crossAlignment($align)};
  justify-content: ${({ $position }) => alignment($position)};

  ${({ $layout, $position, $align }) => $layout !== 'float' ? '' : `
    position: absolute;
    z-index: 0;
    pointer-events: none;
    max-width: calc(100% - 2rem);
    ${floatAnchor($position, $align)}
  `}
`

/*
 * Sizes the artwork. Its alignment has to follow the same position as the
 * Figure around it: when the box ends up wider than the drawing inside it, this
 * is the justify-content that decides where the drawing actually lands.
 *
 * `offset` nudges it from there. It is a transform rather than a margin so it
 * shifts only what you see — the space the illustration occupies stays put, and
 * the text and buttons around it do not move to make room.
 */
const Illustration = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $position }) => alignment($position)};
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  max-width: 100%;
  max-height: 100%;
  transform: translate(${({ $offsetX }) => $offsetX}, ${({ $offsetY }) => $offsetY});
`

const ButtonRow = styled.div`
  grid-area: buttons;
  position: relative;
  z-index: 1;
  align-self: end;
  display: flex;
  flex-wrap: wrap;
  gap: ${base.space[3]};
  justify-content: ${({ $position }) => alignment($position)};
`

/* A step smaller than a form button — this is an invitation, not a submit. */
const CardButton = styled(Button)`
  && {
    font-size: ${widgetSize.label};
    padding-inline: 0.625rem;
    padding-block: 0.25rem;
  }
`

const InfoCardWidget = ({ data, labels }) => {
    const navigate = useNavigate()
    const { t } = useTranslation()

    const background = data?.background || 'neutral'
    const image = data?.image
    const illustration = image?.name ? illustrations[image.name] : null
    const layout = image?.layout || 'inline'
    const buttons = data?.buttons?.items || []

    return (
        <Frame>
            <Card
                $background={background}
                $layout={illustration ? layout : 'inline'}
                $position={image?.position}
            >
                {(labels?.title || labels?.description) && (
                    <Header $position={data?.text?.position}>
                        {labels?.title && <CardTitle>{t(labels.title)}</CardTitle>}
                        {labels?.description && <Description>{t(labels.description)}</Description>}
                    </Header>
                )}

                {illustration && (
                    <Figure $layout={layout} $position={image.position} $align={image.align}>
                        <Illustration
                            $position={image.position}
                            $width={length(image.dimensions?.width, '100%')}
                            $height={length(image.dimensions?.height, '100%')}
                            $offsetX={length(image.offset?.x, '0px')}
                            $offsetY={length(image.offset?.y, '0px')}
                        >
                            {illustration}
                        </Illustration>
                    </Figure>
                )}

                {buttons.length > 0 && (
                    <ButtonRow $position={data?.buttons?.position}>
                        {buttons.map((button) => (
                            <CardButton
                                key={button.id}
                                $variant={button.variant || 'primary'}
                                $marginRight="0"
                                $shadow={false}
                                onClick={() => navigate(button.path)}
                            >
                                {t(button.label)}
                            </CardButton>
                        ))}
                    </ButtonRow>
                )}
            </Card>
        </Frame>
    )
}

export default InfoCardWidget
